import { apiClient } from "./browser";
import type { components } from "./generated/wio-veyra-api";
import type { ApiResponse } from "./types";

type Schemas = components["schemas"];
export type AdminContext = Schemas["TenantAdministrationContextResponse"];
export type Member = Schemas["MemberResponse"];
export type Invitation = Schemas["InvitationResponse"];
export type Role = Schemas["RoleResponse"];
export type TenantSettings = Schemas["TenantSettingsResponse"];
export type Operation = Schemas["OperationResponse"];
export type Avatar = Schemas["AvatarResponse"];
export type StorageAuthorization = Schemas["StorageAuthorizationResponse"];
export type Session = Schemas["SessionResponse"];
export type LoginContext = Schemas["LoginContextResponse"];

interface RequestOptions {
  signal?: AbortSignal;
}

interface TenantOptions extends RequestOptions {
  tenantId: string;
}

interface CommandOptions extends TenantOptions {
  csrfToken: string;
  idempotencyKey: string;
}

const tenantPath = (tenantId: string, suffix: string) =>
  `/tenants/${encodeURIComponent(tenantId)}${suffix}` as const;

export async function getWorkspaceSession(options: RequestOptions = {}) {
  return apiClient.request<Session>({
    operation: "GetWorkspaceSession",
    kind: "query",
    method: "GET",
    path: "/session",
    signal: options.signal,
  });
}

export async function getIdentityContext(options: RequestOptions = {}) {
  return apiClient.request<LoginContext>({
    operation: "GetLoginContext",
    kind: "query",
    method: "GET",
    path: "/identity/context",
    signal: options.signal,
  });
}

export async function getAdminContext(options: TenantOptions) {
  return apiClient.request<AdminContext>({
    operation: "GetTenantAdministrationContext",
    kind: "query",
    method: "GET",
    path: tenantPath(options.tenantId, "/administration/context"),
    signal: options.signal,
  });
}

async function tenantQuery<T>(
  operation: string,
  suffix: string,
  options: TenantOptions,
) {
  return apiClient.request<T>({
    operation,
    kind: "query",
    method: "GET",
    path: tenantPath(options.tenantId, suffix),
    signal: options.signal,
  });
}

async function tenantCommand<T, TBody = undefined>(
  operation: string,
  method: "POST" | "PUT" | "DELETE",
  suffix: string,
  options: CommandOptions,
  body?: TBody,
  expectedVersion?: string,
) {
  return apiClient.request<T, TBody>({
    operation,
    kind: "command",
    method,
    path: tenantPath(options.tenantId, suffix),
    body,
    idempotencyKey: options.idempotencyKey,
    antiforgeryToken: options.csrfToken,
    expectedVersion,
    signal: options.signal,
  });
}

export const listMembers = (options: TenantOptions) =>
  tenantQuery<Member[]>("ListTenantMembers", "/members", options);
export const listInvitations = (options: TenantOptions) =>
  tenantQuery<Invitation[]>("ListTenantInvitations", "/invitations", options);
export const listPermissions = (options: TenantOptions) =>
  tenantQuery<string[]>("ListPermissionCatalogue", "/permissions", options);
export const listRoles = (options: TenantOptions) =>
  tenantQuery<Role[]>("ListTenantRoles", "/roles", options);
export const getSettings = (options: TenantOptions) =>
  tenantQuery<TenantSettings>("GetTenantSettings", "/settings", options);
export const getMyAvatar = (options: TenantOptions) =>
  tenantQuery<Avatar | undefined>("GetMyAvatar", "/avatar/me", options);
export const getOperation = (options: TenantOptions, operationId: string) =>
  tenantQuery<Operation>(
    "GetTenantOperationResult",
    `/operations/${encodeURIComponent(operationId)}`,
    options,
  );

export function createInvitation(
  options: CommandOptions,
  body: Schemas["CreateInvitationRequest"],
) {
  return tenantCommand<Schemas["CreateInvitationResponse"], typeof body>(
    "CreateTenantInvitation",
    "POST",
    "/invitations",
    options,
    body,
  );
}

export const revokeInvitation = (
  options: CommandOptions,
  invitationId: string,
) =>
  tenantCommand<void>(
    "RevokeTenantInvitation",
    "DELETE",
    `/invitations/${encodeURIComponent(invitationId)}`,
    options,
  );
export const revokeMember = (options: CommandOptions, membershipId: string) =>
  tenantCommand<void>(
    "RevokeTenantMembership",
    "DELETE",
    `/members/${encodeURIComponent(membershipId)}`,
    options,
  );

export function assignMemberRoles(
  options: CommandOptions,
  membershipId: string,
  roleIds: string[],
) {
  return tenantCommand<void, Schemas["AssignRolesRequest"]>(
    "AssignTenantMemberRoles",
    "PUT",
    `/members/${encodeURIComponent(membershipId)}/roles`,
    options,
    { roleIds },
  );
}

export function transferOwnership(
  options: CommandOptions,
  membershipId: string,
) {
  return tenantCommand<void, Schemas["TransferOwnershipRequest"]>(
    "TransferTenantOwnership",
    "POST",
    "/ownership/transfer",
    options,
    { newOwnerMembershipId: membershipId },
  );
}

export const createRole = (
  options: CommandOptions,
  body: Schemas["CreateRoleRequest"],
) =>
  tenantCommand<Role, typeof body>(
    "CreateTenantRole",
    "POST",
    "/roles",
    options,
    body,
  );

export function updateRole(
  options: CommandOptions,
  roleId: string,
  body: Schemas["UpdateRoleRequest"],
) {
  return tenantCommand<Role, typeof body>(
    "UpdateTenantRole",
    "PUT",
    `/roles/${encodeURIComponent(roleId)}`,
    options,
    body,
    String(body.expectedVersion),
  );
}

export function updateSettings(
  options: CommandOptions,
  body: Schemas["UpdateTenantSettingsRequest"],
) {
  return tenantCommand<TenantSettings, typeof body>(
    "UpdateTenantSettings",
    "PUT",
    "/settings",
    options,
    body,
    body.expectedVersion === null ? undefined : String(body.expectedVersion),
  );
}

export const createAvatarIntent = (
  options: CommandOptions,
  body: Schemas["AvatarIntentRequest"],
) =>
  tenantCommand<StorageAuthorization, typeof body>(
    "CreateAvatarUploadIntent",
    "POST",
    "/avatar/upload-intents",
    options,
    body,
  );

export const confirmAvatar = (options: CommandOptions, objectId: string) =>
  tenantCommand<Avatar>(
    "ConfirmAvatarUpload",
    "POST",
    `/avatar/upload-intents/${encodeURIComponent(objectId)}/confirm`,
    options,
  );

export function reconcileOperation(
  options: Omit<CommandOptions, "idempotencyKey">,
  operationId: string,
) {
  return apiClient.request<Operation>({
    operation: "ReconcileTenantOperation",
    kind: "query",
    method: "POST",
    path: tenantPath(
      options.tenantId,
      `/operations/${encodeURIComponent(operationId)}/reconcile`,
    ),
    antiforgeryToken: options.csrfToken,
    signal: options.signal,
  });
}

export function activateInvitation(
  body: Schemas["ActivationRequest"],
  csrfToken: string,
  signal?: AbortSignal,
) {
  return apiClient.request<Schemas["ActivationResponse"], typeof body>({
    operation: "ActivateInvitation",
    kind: "command",
    method: "POST",
    path: "/identity/activations",
    body,
    antiforgeryToken: csrfToken,
    signal,
  });
}

export type AdministrationResponse<T> = ApiResponse<T>;
